import { Checkbox, Flex } from "antd";
import TextArea from "antd/es/input/TextArea";
import { FC, useCallback, useMemo, useState } from "react";
import { OrderType, PaymentForm } from "tm-shared/types";

import { useAppDispatch } from "@/hooks/redux";
import { SecondaryText } from "@/shared/ui/Typography";
import { setCheckoutDetails } from "@/store/slices/progress";

import { CarrierModal } from "../../modals/CarrierModal";
import { StyledCheckoutSection, StyledSectionTitle } from "./Styled";

interface CheckoutDetailsProps {
    orderId: string;
    orderType: OrderType;
    paymentForm: PaymentForm;
    isCarrier: boolean;
    isSelfDelivery: boolean;
    isPayOnDelivery: boolean;
    payOnDeliveryComment: string;
    closed: boolean;
}

interface CheckboxConfig {
    id: string;
    label: string;
    checked: boolean;
    disabled?: boolean;
    onChange: (checked: boolean) => void;
}

export const CheckoutDetails: FC<CheckoutDetailsProps> = ({
    orderId,
    orderType,
    paymentForm,
    isCarrier,
    isSelfDelivery,
    isPayOnDelivery,
    payOnDeliveryComment,
    closed,
}) => {
    const dispatch = useAppDispatch();

    const [isCarrierModalOpen, setIsCarrierModalOpen] = useState(false);
    const [pendingCarrierData, setPendingCarrierData] = useState<{
        orderId: string;
        paymentForm: string;
    } | null>(null);
    const [showCommentField, setShowCommentField] =
        useState(!!payOnDeliveryComment);

    const handleCarrierChange = useCallback(
        (checked: boolean) => {
            if (checked) {
                setPendingCarrierData({ orderId, paymentForm });
                setIsCarrierModalOpen(true);
            } else {
                dispatch(
                    setCheckoutDetails({
                        key: "isCarrier",
                        orderId,
                        checked: false,
                    }),
                );
            }
        },
        [dispatch, orderId, paymentForm],
    );

    const handleCarrierConfirm = useCallback(() => {
        if (pendingCarrierData) {
            dispatch(
                setCheckoutDetails({
                    key: "isCarrier",
                    orderId,
                    checked: true,
                }),
            );
        }
        setIsCarrierModalOpen(false);
        setPendingCarrierData(null);
    }, [pendingCarrierData, dispatch, orderId]);

    const handleCommentToggle = useCallback(
        (checked: boolean) => {
            setShowCommentField(checked);
            if (!checked) {
                dispatch(
                    setCheckoutDetails({
                        key: "comment",
                        orderId,
                        checked: false,
                        comment: "",
                    }),
                );
            }
        },
        [dispatch, orderId],
    );

    const payOnDeliveryLabel =
        paymentForm === PaymentForm.Card1
            ? "Дебіторська заборгованість з першого дня"
            : isSelfDelivery
              ? "Оплата по факту"
              : "Оплата в день доставки";

    const checkboxes = useMemo<CheckboxConfig[]>(() => {
        const configs: CheckboxConfig[] = [];

        if (paymentForm === PaymentForm.Cash2) {
            configs.push(
                {
                    id: `carrier-checkbox-${orderId}-${paymentForm}`,
                    label: "Відвантаження із залишків ТА (багажник)",
                    checked: isCarrier,
                    onChange: handleCarrierChange,
                },
                {
                    id: `self-delivery-checkbox-${orderId}-${paymentForm}`,
                    label: "Самостійний вивіз ТТ",
                    checked: isSelfDelivery,
                    onChange: (checked) =>
                        dispatch(
                            setCheckoutDetails({
                                key: "isSelfPickup",
                                orderId,
                                checked,
                            }),
                        ),
                },
            );
        }

        configs.push({
            id: `pay-on-delivery-checkbox-${orderId}-${paymentForm}`,
            label: payOnDeliveryLabel,
            checked: isPayOnDelivery,
            onChange: (checked) =>
                dispatch(
                    setCheckoutDetails({
                        key: "isPayOnDelivery",
                        orderId,
                        checked,
                    }),
                ),
        });

        return configs;
    }, [
        dispatch,
        orderId,
        paymentForm,
        isCarrier,
        isSelfDelivery,
        isPayOnDelivery,
        payOnDeliveryLabel,
        handleCarrierChange,
    ]);

    const renderDisabledCheckbox = (label: string, checked?: boolean) => (
        <Flex gap={10}>
            <Checkbox disabled checked={checked} aria-label={label} />
            <StyledSectionTitle>{label}</StyledSectionTitle>
        </Flex>
    );

    const closedCheckboxes = useMemo(() => {
        const configs = [];

        if (paymentForm === PaymentForm.Cash2) {
            configs.push(
                {
                    label: "Відвантаження із залишків ТА (багажник)",
                    checked: isCarrier,
                },
                { label: "Самостійний вивіз ТТ", checked: isSelfDelivery },
            );
        }

        configs.push(
            { label: payOnDeliveryLabel, checked: isPayOnDelivery },
            { label: "Коментар", checked: !!payOnDeliveryComment?.length },
        );

        return configs;
    }, [
        paymentForm,
        isCarrier,
        isSelfDelivery,
        isPayOnDelivery,
        payOnDeliveryLabel,
        payOnDeliveryComment,
    ]);

    if (orderType === OrderType.R) return null;

    return (
        <>
            <CarrierModal
                isModalCarrierOpened={isCarrierModalOpen}
                onConfirmCarrier={handleCarrierConfirm}
                onCancelCarrier={() => setIsCarrierModalOpen(false)}
            />

            {!closed ? (
                <StyledCheckoutSection gap={12} align="flex-start">
                    {checkboxes.map(
                        ({ id, label, checked, disabled, onChange }) => (
                            <Flex
                                key={id}
                                justify="center"
                                align="center"
                                gap={4}
                            >
                                <Checkbox
                                    id={id}
                                    checked={checked}
                                    disabled={disabled}
                                    onChange={(e) => onChange(e.target.checked)}
                                >
                                    <SecondaryText>{label}</SecondaryText>
                                </Checkbox>
                            </Flex>
                        ),
                    )}

                    <Flex justify="flex-start" gap={4}>
                        <Checkbox
                            id={`comment-checkbox-${orderId}`}
                            checked={showCommentField}
                            onChange={(e) =>
                                handleCommentToggle(e.target.checked)
                            }
                        >
                            <SecondaryText>Коментар</SecondaryText>
                        </Checkbox>
                    </Flex>

                    {showCommentField && (
                        <TextArea
                            maxLength={256}
                            value={payOnDeliveryComment}
                            onChange={(e) =>
                                dispatch(
                                    setCheckoutDetails({
                                        key: "comment",
                                        orderId,
                                        checked: true,
                                        comment: e.target.value,
                                    }),
                                )
                            }
                        />
                    )}
                </StyledCheckoutSection>
            ) : (
                <StyledCheckoutSection gap={10}>
                    {closedCheckboxes.map(({ label, checked }) =>
                        renderDisabledCheckbox(label, checked),
                    )}
                    {!!payOnDeliveryComment?.length && (
                        <TextArea
                            disabled
                            value={payOnDeliveryComment}
                            aria-label="Коментар до оплати"
                            rows={3}
                        />
                    )}
                </StyledCheckoutSection>
            )}
        </>
    );
};
